<template>
    <div>
        <h3 class="text-h6 mb-1">Recalculate Device Subscriptions</h3>
        <p class="text-body-2 text-medium-emphasis mb-4">
            Recalculates <code>subscription_end_date</code> for all devices based on their paid invoices.
            For each device, sums the total subscription duration from all paid invoices and sets the end
            date accordingly.
        </p>

        <v-alert v-if="!result" type="info" variant="tonal" density="compact" class="mb-4">
            This is safe to run multiple times - it recalculates from scratch each time.
        </v-alert>

        <v-btn color="primary" variant="flat" :loading="loading" prepend-icon="mdi-play"
               @click="run" class="mb-4">
            Run
        </v-btn>

        <v-alert v-if="errorMessage" type="error" variant="tonal" density="compact" class="mb-3">
            {{ errorMessage }}
        </v-alert>

        <template v-if="result">
            <v-alert :type="result.errors > 0 ? 'warning' : 'success'" variant="tonal" density="compact" class="mb-3">
                Updated {{ result.updated }} devices,
                skipped {{ result.skipped }},
                errors {{ result.errors }}
            </v-alert>

            <v-data-table
                v-if="result.details?.length"
                :headers="headers"
                :items="result.details"
                density="compact"
                class="elevation-1"
                :items-per-page="20"
            >
                <template v-slot:item.old_end_date="{ item }">
                    <span :class="{'text-red': isExpired(item.old_end_date)}">
                        {{ item.old_end_date || 'NULL' }}
                    </span>
                </template>
                <template v-slot:item.new_end_date="{ item }">
                    <span class="text-green font-weight-medium">{{ item.new_end_date }}</span>
                </template>
            </v-data-table>
        </template>
    </div>
</template>

<script setup>
import {ref} from 'vue';
import {send} from '@/composables/apiClient';

const loading = ref(false);
const result = ref(null);
const errorMessage = ref(null);

const headers = [
    {title: 'Device', key: 'slno', sortable: true},
    {title: 'Company', key: 'company_id', sortable: true},
    {title: 'Paid Invoices', key: 'paid_invoices', sortable: true},
    {title: 'Months', key: 'total_months', sortable: true},
    {title: 'Start Date', key: 'start_date', sortable: true},
    {title: 'Old End Date', key: 'old_end_date', sortable: true},
    {title: 'New End Date', key: 'new_end_date', sortable: true},
];

const isExpired = (date) => {
    if (!date) return true;
    return new Date(date) < new Date();
};

const run = async () => {
    if (!confirm('This will recalculate subscription_end_date for all devices based on paid invoices. Continue?')) {
        return;
    }
    loading.value = true;
    result.value = null;
    errorMessage.value = null;
    try {
        const response = await send('post', 'admin/tools/recalculate-subscriptions');
        result.value = response.data?.data;
    } catch (e) {
        // Surface the real reason instead of a meaningless "errors: 1".
        errorMessage.value = e?.response?.data?.message
            || e?.message
            || 'The recalculation request failed. Please try again or check the server logs.';
    } finally {
        loading.value = false;
    }
};
</script>
